'use client'

import { UI_IDS } from '@shared/constants'
import { useHomeUrl } from '@shared/routes'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { match, P } from 'ts-pattern'

import { Box, Flex, Line24pxBackIcon, Typography } from '@podo-app/design-system-temp'

import { SPA_NAV_COUNT_KEY } from './spa-history-tracker'

// 구(舊) 타입: 기존 사용처와 하위호환을 위해 유지
type BackButtonConfig =
  | { type: 'url'; url: string }
  | { type: 'action'; action: () => void }
  | { type: 'history' }
  | { type: 'none' }

type ExtraButtonConfig =
  | { type: 'url'; icon: React.ReactNode; url: string }
  | { type: 'action'; action: () => void; icon: React.ReactNode }
  | { type: 'popup'; icon: React.ReactNode; url: string }
  | { type: 'trigger'; icon: React.ReactNode }

// 신(新) 타입: 쓰는 쪽을 단순화한 API
type BackProp = true | 'history' | string | (() => void) | BackButtonConfig
type RightButtonProp = {
  icon: React.ReactNode
  href?: string
  onPress?: () => void
  newWindow?: boolean
  disabled?: boolean
}

// 통합된 TopNavigation Props (신 API 중심, 구 API는 하위호환 매핑)
interface TopNavigationProps {
  title?: React.ReactNode
  icon?: React.ReactNode
  back?: BackProp
  rightButton?: RightButtonProp
  // deprecated: 하위호환용
  extraButton?: ExtraButtonConfig
  backButton?: BackButtonConfig
  variant?: 'default' | 'popup'
}

const isExternalUrl = (url: string): boolean => {
  return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')
}

// 백 버튼 렌더링 컴포넌트
const BackButton = ({ config }: { config: BackButtonConfig }) => {
  const router = useRouter()
  const homeUrl = useHomeUrl()

  if (config.type === 'none') {
    return null
  }

  if (config.type === 'history') {
    const handleHistoryClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault()

      // Link href keeps cold-start fallback usable even if the first click happens before hydration.
      const count = typeof sessionStorage !== 'undefined' ? Number(sessionStorage.getItem(SPA_NAV_COUNT_KEY) ?? '0') : 0
      if (count > 1) {
        router.back()
      } else {
        router.replace(homeUrl)
      }
    }

    return (
      <Link href={homeUrl} onClick={handleHistoryClick} data-testid={UI_IDS.btn.back}>
        <Line24pxBackIcon />
      </Link>
    )
  }

  const handleClick = () => {
    switch (config.type) {
      case 'action':
        config.action()
        break
      case 'url':
        if (!isExternalUrl(config.url)) {
          router.push(config.url)
        }
        break
    }
  }

  // 외부 URL인 경우 a 태그 사용
  if (config.type === 'url' && isExternalUrl(config.url)) {
    return (
      <a href={config.url} rel="noopener noreferrer" data-testid={UI_IDS.btn.back}>
        <Line24pxBackIcon />
      </a>
    )
  }

  // 내부 URL이고 정적 링크인 경우 Link 사용
  if (config.type === 'url') {
    return (
      <Link href={config.url} data-testid={UI_IDS.btn.back}>
        <Line24pxBackIcon />
      </Link>
    )
  }

  // 그 외에는 버튼 사용
  return (
    <button onClick={handleClick} data-testid={UI_IDS.btn.back}>
      <Line24pxBackIcon />
    </button>
  )
}

// 우측 버튼 렌더링 컴포넌트
const RightButton = ({ button }: { button: RightButtonProp }) => {
  if (!button) {
    return null
  }
  const { icon, href, onPress, newWindow, disabled = false } = button

  if (href) {
    const external = isExternalUrl(href)
    if (external || newWindow) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {icon}
        </a>
      )
    }

    return <Link href={href}>{icon}</Link>
  }

  if (onPress) {
    return (
      <button onClick={onPress} disabled={disabled}>
        {icon}
      </button>
    )
  }

  return null
}

// 구/신 API -> 내부 표준 타입으로 정규화
const normalizeBack = (back?: BackProp): BackButtonConfig => {
  if (!back) {
    return { type: 'none' }
  }
  if (typeof back === 'boolean') {
    return back ? { type: 'history' } : { type: 'none' }
  }
  if (typeof back === 'string') {
    // 'history' 문자열을 특별 취급
    return back === 'history' ? { type: 'history' } : { type: 'url', url: back }
  }
  if (typeof back === 'function') {
    return { type: 'action', action: back }
  }
  // 이미 구 타입인 경우
  return back
}

const normalizeRight = (
  right?: RightButtonProp | undefined,
  // 구 API(extraButton): 'popup' | 'url'만 하위호환 (action은 아이콘 없음)
  legacy?: ExtraButtonConfig | undefined,
): RightButtonProp | undefined => {
  if (right) {
    return right
  }
  if (legacy && (legacy.type === 'popup' || legacy.type === 'url')) {
    return { icon: legacy.icon, href: legacy.url }
  }
  if (legacy && legacy.type === 'action') {
    return { icon: legacy.icon, onPress: legacy.action }
  }
  if (legacy && legacy.type === 'trigger') {
    return { icon: legacy.icon }
  }
  return undefined
}

// 메인 TopNavigation 컴포넌트
export const TopNavigation = ({
  title,
  icon,
  back,
  rightButton,
  extraButton,
  backButton,
  variant: _variant,
}: TopNavigationProps) => {
  const normalizedBack = normalizeBack(backButton ?? back)
  const normalizedRight = normalizeRight(rightButton, extraButton)

  return (
    <Flex className="relative justify-between px-5 py-3">
      {/* 백 버튼 */}
      {normalizedBack.type !== 'none' && (
        <Box className="absolute left-5 top-1/2 -translate-y-1/2 inline-flex">
          <BackButton config={normalizedBack} />
        </Box>
      )}

      {/* 제목 영역 - 내용이 없어도 최소 높이 유지 */}
      <Flex className="w-full justify-center gap-2 items-center min-h-6">
        {icon}
        {title && <Typography size="h1">{title}</Typography>}
      </Flex>

      {/* 우측 버튼 */}
      {normalizedRight && (
        <Box className="absolute right-5 top-1/2 -translate-y-1/2">
          <RightButton button={normalizedRight} />
        </Box>
      )}
    </Flex>
  )
}

// 기존 컴포넌트들을 새로운 TopNavigation으로 대체하는 편의 함수들
export const FullTopNavigation = (
  props: {
    title: React.ReactNode
    extraButton?: ExtraButtonConfig
    icon?: React.ReactNode
  } & ({ backUrl?: string } | { historyGoBack: true }),
) => {
  let backButton: BackButtonConfig = { type: 'none' }

  if ('historyGoBack' in props) {
    backButton = { type: 'history' }
  } else if ('backUrl' in props && props.backUrl) {
    backButton = { type: 'url', url: props.backUrl }
  }

  return (
    <TopNavigation
      title={props.title}
      icon={props.icon}
      back={backButton}
      rightButton={
        props.extraButton &&
        match(props.extraButton)
          .with({ type: P.union('popup', 'url') }, ({ icon, url }) => ({ icon, url }))
          .with({ type: 'trigger' }, ({ icon }) => ({ icon }))
          .with({ type: 'action' }, (actionExtraButton) => ({
            icon: actionExtraButton.icon,
            onPress: actionExtraButton.action,
          }))
          .exhaustive()
      }
    />
  )
}

export const TopBackNavigation = ({
  title,
  extraButton,
  ...props
}:
  | {
      title: React.ReactNode
      extraButton?: ExtraButtonConfig
      backButtonAction: () => void
    }
  | {
      title: React.ReactNode
      extraButton?: ExtraButtonConfig
      backUrl: string
    }) => {
  const isActionBack = 'backButtonAction' in props

  return (
    <TopNavigation
      title={title}
      back={isActionBack ? { type: 'action', action: props.backButtonAction } : { type: 'url', url: props.backUrl }}
      rightButton={
        extraButton && (extraButton.type === 'popup' || extraButton.type === 'url')
          ? { icon: extraButton.icon, href: extraButton.url }
          : undefined
      }
    />
  )
}

export const BackTopNavigation = ({ backUrl }: { backUrl: string }) => {
  return <TopNavigation back={backUrl} />
}

export const TitleTopNavigation = ({
  title,
  extraButton,
  icon,
}: {
  title: React.ReactNode
  extraButton?: ExtraButtonConfig
  icon?: React.ReactNode
}) => {
  return (
    <TopNavigation
      title={title}
      icon={icon}
      rightButton={
        extraButton && (extraButton.type === 'popup' || extraButton.type === 'url')
          ? { icon: extraButton.icon, href: extraButton.url }
          : undefined
      }
    />
  )
}

export const PopupTopNavigation = ({
  title,
  extraButton,
}: {
  title: React.ReactNode
  extraButton?: ExtraButtonConfig
  linkUrl?: string
}) => {
  return (
    <TopNavigation
      title={title}
      rightButton={
        extraButton && (extraButton.type === 'popup' || extraButton.type === 'url')
          ? { icon: extraButton.icon, href: extraButton.url }
          : undefined
      }
      variant="popup"
    />
  )
}

export const HistoryBackNavigation = () => {
  return <TopNavigation back={'history'} />
}
